import React, { useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";
import { useParams } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  ButtonPrimary,
  ButtonPrimary1,
} from "../../../components/utils";
import SearchInput from "../../../components/shared/SearchInput";
import { BlackScholesV2 } from "../../../types/blackScholes";
import { formatDisplayDate } from "../../../utils/date";
import {
  formatCurrency,
  getCurrencyType,
} from "../../../utils/currencyFormatter";

interface BlackScholesSummaryProps {
  grantScopeData: BlackScholesV2 | undefined;
  onBack: () => void;
  onFinish: () => void;
}

export const BlackScholesSummary: React.FC<BlackScholesSummaryProps> = ({
  grantScopeData,
  onBack,
  onFinish,
}) => {
  const gridApi = useRef<GridApi | null>(null);
  const { id } = useParams();
  const currencyType = getCurrencyType();
  const [searchQuery, setSearchQuery] = useState("");

  const rowData = useMemo(() => {
    if (!grantScopeData?.calculatedData) return [];
    const rows = Object.entries(grantScopeData.calculatedData).map(
      ([key, data]) => {
        let grantDate = key;
        let strikePrice;

        if (grantScopeData.groupByGrantDateAndStrikePrice) {
          const parts = key.split("__");
          if (parts.length === 2) {
            grantDate = parts[0];
            strikePrice = Number(parts[1]);
          }
        }

        return {
          grantDate,
          strikePrice,
          ...data,
          ...data.values, // Flatten the values object for the grid
        };
      }
    );
    return rows;
  }, [
    grantScopeData?.calculatedData,
    grantScopeData?.groupByGrantDateAndStrikePrice,
  ]);

  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        headerName: "Grant Date",
        field: "grantDate",
        pinned: "left",
        valueFormatter: (params) => formatDisplayDate(params.value),
        filter: "agDateColumnFilter",
        getQuickFilterText: (params) => formatDisplayDate(params.value),
      },
    ];

    if (grantScopeData?.groupByGrantDateAndStrikePrice) {
      cols.push({
        headerName: "Strike Price",
        field: "strikePrice",
        pinned: "left",
        valueFormatter: (params) =>
          params.value ? formatCurrency(params.value, currencyType) : "-",
      });
    }

    cols.push(
      ...([
        {
          headerName: "Valuation Date",
          field: "valuationDate",
          valueFormatter: (params) =>
            params.value ? formatDisplayDate(params.value) : "-",
          getQuickFilterText: (params) =>
            params.value ? formatDisplayDate(params.value) : "-",
        },
        {
          headerName: "FMV (Option Value)",
          field: "fmv",
          valueFormatter: (params) =>
            params.value ? formatCurrency(params.value, currencyType) : "-",
        },
        {
          headerName: "Stock Price",
          field: "stockPrice",
          valueFormatter: (params) =>
            params.value ? formatCurrency(params.value, currencyType) : "-",
        },
        {
          headerName: "Volatility",
          field: "volatility",
          valueFormatter: (params) =>
            params.value
              ? `${Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}%`
              : "-",
        },
        {
          headerName: "Risk-Free Rate",
          field: "riskFreeRate",
          valueFormatter: (params) =>
            params.value
              ? `${Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}%`
              : "-",
        },
        {
          headerName: "Dividend Yield",
          field: "dividendYield",
          valueFormatter: (params) =>
            params.value
              ? `${Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}%`
              : "-",
        },
        {
          headerName: "WAVP",
          field: "wavp",
          valueFormatter: (params) =>
            params.value
              ? Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                })
              : "-",
        },
        {
          headerName: "Orig. Maturity (Yrs)",
          field: "originalTimeToMaturity",
          valueFormatter: (params) =>
            params.value
              ? Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "-",
        },
        {
          headerName: "d1",
          field: "d1",
          valueFormatter: (params) =>
            params.value
              ? Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                })
              : "-",
        },
        {
          headerName: "d2",
          field: "d2",
          valueFormatter: (params) =>
            params.value
              ? Number(params.value).toLocaleString(currencyType, {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                })
              : "-",
        },
      ] as ColDef[])
    );

    return cols;
  }, [grantScopeData?.groupByGrantDateAndStrikePrice, currencyType]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      wrapText: true,
      autoHeight: true,
      minWidth: 150,
    }),
    []
  );

  if (!grantScopeData) {
    return <Box className="p-6">Loading data...</Box>;
  }

  return (
    <Box className="w-full">
      <VStack className="">
        <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
          <HStack className="flex justify-between font-semibold text-lg1 text-black-501">
            Valuation Calculation Summary
          </HStack>
          <p className="text-gray-401 font-medium text-sm3">
            Review the calculated Black-Scholes values for each grant group.
          </p>
        </VStack>

        <VStack className="gap-6 w-full p-6">
          <Box className="bg-gray-50 p-4 rounded-md border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Valuation Inputs
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block">Valuation Date</span>
                <span className="font-medium">
                  {formatDisplayDate(grantScopeData.valuationDate)}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Period</span>
                <span className="font-medium">
                  {grantScopeData.startDate
                    ? formatDisplayDate(grantScopeData.startDate)
                    : "N/A"}{" "}
                  -{" "}
                  {grantScopeData.endDate
                    ? formatDisplayDate(grantScopeData.endDate)
                    : "N/A"}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Options Expiry</span>
                <span className="font-medium">
                  {grantScopeData.optionsExpiryFromPlanMaturity
                    ? "From Plan Maturity"
                    : `${grantScopeData.optionsExpiryYears} Years`}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Grouping Method</span>
                <span className="font-medium">
                  {grantScopeData.groupByGrantDateAndStrikePrice
                    ? "Grant Date & Strike Price"
                    : "Grant Date"}
                </span>
              </div>
              <div className="col-span-full border-t border-gray-200 pt-3 mt-1">
                <span className="text-gray-500 block mb-2">
                  Applied Filters
                </span>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {grantScopeData.filters?.planIds?.length ? (
                    <div>
                      <span className="font-medium text-xs text-gray-500 uppercase mr-1">
                        Plans:
                      </span>
                      {grantScopeData.filters.planIds
                        .map((p) => p.name)
                        .join(", ")}
                    </div>
                  ) : null}
                  {grantScopeData.filters?.subsidiaryIds?.length ? (
                    <div>
                      <span className="font-medium text-xs text-gray-500 uppercase mr-1">
                        Subsidiaries:
                      </span>
                      {grantScopeData.filters.subsidiaryIds
                        .map((p) => p.name)
                        .join(", ")}
                    </div>
                  ) : null}
                  {grantScopeData.filters?.employmentType?.length ? (
                    <div>
                      <span className="font-medium text-xs text-gray-500 uppercase mr-1">
                        Employment Type:
                      </span>
                      {grantScopeData.filters.employmentType.join(", ")}
                    </div>
                  ) : null}
                  {grantScopeData.filters?.employeeStatus?.length ? (
                    <div>
                      <span className="font-medium text-xs text-gray-500 uppercase mr-1">
                        Status:
                      </span>
                      {grantScopeData.filters.employeeStatus.join(", ")}
                    </div>
                  ) : null}
                  {grantScopeData.filters?.grantStates?.length ? (
                    <div>
                      <span className="font-medium text-xs text-gray-500 uppercase mr-1">
                        Grant State:
                      </span>
                      {grantScopeData.filters.grantStates.join(", ")}
                    </div>
                  ) : null}
                  {!grantScopeData.filters?.planIds?.length &&
                  !grantScopeData.filters?.subsidiaryIds?.length &&
                  !grantScopeData.filters?.employmentType?.length &&
                  !grantScopeData.filters?.employeeStatus?.length &&
                  !grantScopeData.filters?.grantStates?.length ? (
                    <div>No filters applied</div>
                  ) : null}
                </div>
              </div>
            </div>
          </Box>

          <HStack className="gap-6 w-full items-end justify-between">
            <Box className="flex-1 max-w-sm">
              <SearchInput
                className=""
                placeholder="Search..."
                onChange={(e: any) => {
                  setSearchQuery(e.target.value);
                  gridApi.current?.setQuickFilter(e.target.value);
                }}
              />
            </Box>
          </HStack>

          <Box className="w-full ag-theme-material" style={{ height: "500px" }}>
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={(params) => {
                gridApi.current = params.api;
                params.api.sizeColumnsToFit();
              }}
              pagination={true}
              paginationPageSize={10}
              animateRows={true}
              suppressRowTransform={true}
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">No Rows To Show</span>'
              }
            />
          </Box>

          <HStack className="justify-between pt-4 border-t border-gray-200 mt-4">
            <ButtonPrimary1 onClick={onBack}>Back</ButtonPrimary1>
            <ButtonPrimary onClick={onFinish}>
              {id ? "Save Changes" : "Save"}
            </ButtonPrimary>
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
};
